/**
 * VantageWorkbook Store
 *
 * Single source of truth for a Vantage workbook: persisted config, evidence table
 * shape, tree selection, extensions (toolbar + column config), and condition
 * monitor state. Sections: STATE, ACTIONS, SELECTORS (derived), then exported
 * hooks for React.
 */

import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { createSelector } from 'reselect';
import { type SetStoreState, createStore, createStoreInstance } from '@/stores/createStore';
import {
  findConditionIdColumnNames,
  findConditions,
} from '@/vantage/components/evidence_table/helpers/conditionFinders';
import { VantageNames } from '@/vantage/constants';
import { addCharacteristicsForPurviewFields } from '@/vantage/extension.utilities';
import { getTableColumns } from '@/vantage/vantageWorkbook.utilities';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import type { InstanceConfig, TableColumns, TableConfig } from '@/vantage/types/TableConfig.types';
import type { StoppedConditionMonitorState } from '@/vantage/types/VantageWorkbook.types';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';

// ========================================
// STATE
// ========================================

export interface VantageWorkbookState {
  /** Persisted workbook config from backend, drives context fetch and column derivation */
  workbookConfig?: InstanceConfig;
  /** Bumped when user edits context so evidence context is refetched */
  contextLastModified: number;
  /** Current tree selection (drives active context and which condition IDs are in scope) */
  selectedNodes: SimplifiedNode[];
  /** Extension names that are expanded or pinned in the toolbar */
  activeExtensions: string[];
  /** Backend table definition, source for table structure and condition monitor config */
  evidenceTableDefinition?: TableDefinitionOutputV1;
  /** Condition monitor job progress percentage (0 to 1) */
  conditionMonitorJobPercentComplete?: number;
  /** Snapshot when monitor was stopped (enabled/archived, job id) for resume and stopped-state UI */
  stoppedConditionMonitorState?: StoppedConditionMonitorState;
  /** Condition IDs that pass the evidence table filters, synced from the table */
  filteredConditionIds: string[];
}

const initialState: VantageWorkbookState = {
  workbookConfig: undefined,
  contextLastModified: 0,
  selectedNodes: [],
  activeExtensions: [],
  evidenceTableDefinition: undefined,
  conditionMonitorJobPercentComplete: undefined,
  stoppedConditionMonitorState: undefined,
  filteredConditionIds: [],
};

// ========================================
// STORE
// ========================================

const vantageWorkbookActionsFactory = (set: SetStoreState<VantageWorkbookState>) => ({
  /**
   * Update workbook config and evidence table definition.
   */
  setWorkbook: (payload: { workbookConfig?: InstanceConfig; evidenceTableDefinition?: TableDefinitionOutputV1 }) => {
    set((draft) => {
      draft.workbookConfig = payload.workbookConfig;
      draft.evidenceTableDefinition = payload.evidenceTableDefinition;
    });
  },

  /**
   * Sets contextLastModified to current timestamp.
   */
  markContextModified: () => {
    set((draft) => {
      draft.contextLastModified = Date.now();
    });
  },

  /**
   * Set extensions in workbook config.
   * Filters to only enabled extensions.
   */
  setExtensions: (extensions: ExtensionSchema[]) => {
    set((draft) => {
      if (draft.workbookConfig) {
        draft.workbookConfig.extensions = extensions.filter(({ enabled }) => enabled);
      }
    });
  },

  setActiveExtensions: (activeExtensions: string[]) => {
    set((draft) => {
      draft.activeExtensions = activeExtensions;
    });
  },

  setSelectedNodes: (selectedNodes: SimplifiedNode[]) => {
    set((draft) => {
      draft.selectedNodes = selectedNodes;
    });
  },

  /**
   * Set evidence table definition from backend.
   */
  setEvidenceTableDefinition: (evidenceTableDefinition: TableDefinitionOutputV1) => {
    set((draft) => {
      draft.evidenceTableDefinition = evidenceTableDefinition;
    });
  },

  setConditionMonitorJobPercentComplete: (percentComplete: number | undefined) => {
    set((draft) => {
      draft.conditionMonitorJobPercentComplete = percentComplete;
    });
  },

  setIncludeExtinct: (includeExtinct: boolean) => {
    set((draft) => {
      if (draft.workbookConfig) {
        draft.workbookConfig.includeExtinct = includeExtinct;
      }
    });
  },

  setIsManualEventsEnabled: (isManualEventsEnabled: boolean) => {
    set((draft) => {
      if (draft.workbookConfig) {
        draft.workbookConfig.isManualEventsEnabled = isManualEventsEnabled;
      }
    });
  },

  setStoppedConditionMonitorState: (stoppedConditionMonitorState: StoppedConditionMonitorState | undefined) => {
    set((draft) => {
      draft.stoppedConditionMonitorState = stoppedConditionMonitorState;
    });
  },

  setFilteredConditionIds: (filteredConditionIds: string[]) => {
    set((draft) => {
      draft.filteredConditionIds = filteredConditionIds;
    });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(() => initialState);
  },
});

const [VantageWorkbookStoreProvider, useVantageWorkbookStore, useVantageWorkbookSelector] = createStore(
  'VantageWorkbook',
  initialState,
  vantageWorkbookActionsFactory,
);

/**
 * Creates a new store instance for tests that must assert exact store state or when
 * porting Flux store tests that call actions imperatively.
 *
 * Prefer `<TestStoreProvider>` or `<*StoreProvider>` in tests and validate behavior
 * through UI interactions + DOM assertions whenever possible.
 *
 * Use with `<TestStoreProvider>` and control state via `setState`.
 *
 * @example
 * ```tsx
 * const store = createTestVantageWorkbookStore();
 * render(<TestStoreProvider vantageWorkbookStore={store}><MyComponent /></TestStoreProvider>);
 * act(() => store.setState({ workbookConfig: mockConfig }));
 * ```
 *
 * NOTE: Only use this function in **test files**.
 *
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestVantageWorkbookStore(overrides?: Partial<VantageWorkbookState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestVantageWorkbookStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, vantageWorkbookActionsFactory);
}

// ========================================
// EXPORTS: Provider
// ========================================

export { VantageWorkbookStoreProvider };

// ========================================
// SELECTORS
// ========================================

/**
 * Base + extension columns derived from evidenceTableDefinition and workbookConfig
 */
const selectTableColumns = createSelector(
  (state: VantageWorkbookState) => state.evidenceTableDefinition,
  (state: VantageWorkbookState) => state.workbookConfig,
  (evidenceTableDefinition, workbookConfig): TableColumns | undefined => {
    if (!evidenceTableDefinition || !workbookConfig) return undefined;
    return getTableColumns(evidenceTableDefinition.columnDefinitions, workbookConfig.extensions);
  },
);

/**
 * Column names that are condition references (derived from tableColumns condition_id + reference columns)
 */
const selectConditionsList = createSelector(selectTableColumns, (tableColumns): string[] | undefined => {
  if (!tableColumns) return undefined;
  return findConditions(tableColumns, findConditionIdColumnNames(tableColumns));
});

/**
 * Workbook extensions with Notes augmented by tableConfig purview fields (extra context characteristics)
 */
const selectExtensions = createSelector(
  (state: VantageWorkbookState) => state.workbookConfig?.extensions,
  (state: VantageWorkbookState) => state.workbookConfig?.tableConfig?.purviewFields,
  (extensions, purviewFields): ExtensionSchema[] => {
    if (!extensions) return [];

    return extensions.map((ext) =>
      ext.name === VantageNames.Extensions.Notes ? addCharacteristicsForPurviewFields(ext, purviewFields ?? []) : ext,
    );
  },
);

/**
 * User-defined context column names (from extensions with UserDefinedContextNamespace prefix)
 * Filters extensions by user-defined namespace.
 */
const selectUserDefinedContextColumnNames = createSelector(selectExtensions, (extensions): string[] => {
  return extensions
    .filter((extension) => extension.name.startsWith(SeeqNames.SeeqContextNameSpace.UserDefinedContextNamespace))
    .map((extension) => extension.name);
});

const REASON_CODE_COLUMN_PREFIX = `${SeeqNames.SeeqContextNameSpace.ReasonCodeNamespace}.`;
const REASON_CODE_AUTHOR_VIRTUAL_COLUMN = `${REASON_CODE_COLUMN_PREFIX}author`;

const toReasonCodeVirtualColumnName = (column: { colDefName?: string; category?: string }): string =>
  column.colDefName === 'Author'
    ? REASON_CODE_AUTHOR_VIRTUAL_COLUMN
    : `${REASON_CODE_COLUMN_PREFIX}${column.category!}`;

/**
 * Reason code virtual column names, all prefixed with "reasonCode." so the backend can
 * identify them. The "Author" column uses "reasonCode.author"; label columns use
 * "reasonCode.{category}". The backend strips the prefix for label lookups and uses
 * the sibling categories for the author creator lookup.
 */
const selectReasonCodeColumnNames = createSelector(selectExtensions, (extensions): string[] => {
  return extensions
    .filter((extension) => extension.name === VantageNames.Extensions.ReasonCodes)
    .flatMap((extension) => extension.uiConfig?.columns ?? [])
    .filter((column) => column.category)
    .map(toReasonCodeVirtualColumnName);
});

/**
 * Maps reason code column display names (colDefName) to their SSRM virtual column names.
 * Used to resolve sort/filter/groupBy column names to the backend virtual column identifiers.
 */
const selectReasonCodeColumnNameMap = createSelector(selectExtensions, (extensions): Record<string, string> => {
  return Object.fromEntries(
    extensions
      .filter((extension) => extension.name === VantageNames.Extensions.ReasonCodes)
      .flatMap((extension) => extension.uiConfig?.columns ?? [])
      .filter((column) => column.colDefName && column.category)
      .map((column) => [column.colDefName!, toReasonCodeVirtualColumnName(column)]),
  );
});

// Simple selectors (no memoization needed for primitives/direct references)
const selectTableConfig = (state: VantageWorkbookState): TableConfig | undefined => state.workbookConfig?.tableConfig;
const selectTableIds = (state: VantageWorkbookState): Record<string, string> | undefined =>
  state.workbookConfig?.tableIds;
const selectWorkbookId = (state: VantageWorkbookState): string | undefined => state.workbookConfig?.workbookId;

/**
 * Whether to include extinct capsules. Defaults true until config loads (some workbooks lack "Last Capsule State").
 */
const selectIncludeExtinct = (state: VantageWorkbookState): boolean => state.workbookConfig?.includeExtinct ?? true;
const selectIsManualEventsEnabled = (state: VantageWorkbookState): boolean =>
  state.workbookConfig?.isManualEventsEnabled ?? false;

// ========================================
// EXPORTS: State Hooks (Fine-Grained Selectors)
// ========================================

/**
 * Store actions (stable reference, does not trigger re-renders)
 */
export const useVantageWorkbookActions = () => useVantageWorkbookStore().actions;

/** Persisted workbook config from backend */
export const useWorkbookConfig = () => useVantageWorkbookSelector((state) => state.workbookConfig);

/** Extensions with Notes + purview-derived context characteristics */
export const useExtensions = () => useVantageWorkbookSelector(selectExtensions);

/** Map of logical table names to backend table IDs */
export const useTableIds = () => useVantageWorkbookSelector(selectTableIds);

/** Base + extension column config (drives evidence table columns) */
export const useTableColumns = () => useVantageWorkbookSelector(selectTableColumns);

/** Condition reference column names derived from tableColumns */
export const useConditionsList = () => useVantageWorkbookSelector(selectConditionsList);

/** Backend evidence table definition (structure + condition monitor config) */
export const useEvidenceTableDefinition = () => useVantageWorkbookSelector((state) => state.evidenceTableDefinition);

/** Condition IDs currently passing evidence table filters */
export const useFilteredConditionIds = () => useVantageWorkbookSelector((state) => state.filteredConditionIds);

/** Condition monitor job progress in [0, 1] */
export const useConditionMonitorJobPercentComplete = () =>
  useVantageWorkbookSelector((state) => state.conditionMonitorJobPercentComplete);

/** Last saved state when monitor was stopped (for resume and disabled/archived UI) */
export const useStoppedConditionMonitorState = () =>
  useVantageWorkbookSelector((state) => state.stoppedConditionMonitorState);

/** Currently selected nodes in the workbook tree */
export const useSelectedNodes = () => useVantageWorkbookSelector((state) => state.selectedNodes);

/** Extension names expanded or pinned in the toolbar */
export const useActiveExtensions = () => useVantageWorkbookSelector((state) => state.activeExtensions);

/** Timestamp bumped when context is edited (used to refetch evidence context) */
export const useContextLastModified = () => useVantageWorkbookSelector((state) => state.contextLastModified);

/** Table config (purview fields, date range, context mappings) */
export const useTableConfig = () => useVantageWorkbookSelector(selectTableConfig);

/** Workbook ID from persisted config */
export const useWorkbookId = () => useVantageWorkbookSelector(selectWorkbookId);

/** Include extinct capsules (default true until config loads) */
export const useIncludeExtinct = () => useVantageWorkbookSelector(selectIncludeExtinct);

/** Whether manual events are enabled for the workbook */
export const useIsManualEventsEnabled = () => useVantageWorkbookSelector(selectIsManualEventsEnabled);

/** User-defined context extension names (for context column pickers) */
export const useUserDefinedContextColumnNames = () => useVantageWorkbookSelector(selectUserDefinedContextColumnNames);

/** Reason code SSRM virtual column names (e.g., "reasonCode.<category>", "reasonCode.author") */
export const useReasonCodeColumnNames = () => useVantageWorkbookSelector(selectReasonCodeColumnNames);

/** Map from reason code display name to SSRM virtual column name (for sort/filter resolution) */
export const useReasonCodeColumnNameMap = () => useVantageWorkbookSelector(selectReasonCodeColumnNameMap);
